import { useState, useEffect } from "react";
import api, { formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { Printer, Plus, Trash2, Copy, RefreshCw, Wifi, WifiOff, RotateCcw, Pencil } from "lucide-react";

const empty = { name: "", station: "cucina", ip_address: "", port: 9100, enabled: false };

export default function PrintersCard() {
  const [printers, setPrinters] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [token, setToken] = useState("");
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [p, t] = await Promise.all([api.get("/printers"), api.get("/settings/print-agent-token")]);
      setPrinters(p.data || []);
      setToken(t.data?.token || "");
    } catch { /* noop */ }
    try {
      const { data } = await api.get("/print/jobs", { params: { status: "FAILED" } });
      setJobs(data || []);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); const id = setInterval(load, 8000); return () => clearInterval(id); }, []);

  const reset = () => { setForm(empty); setEditId(null); };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Nome stampante obbligatorio"); return; }
    setBusy(true);
    try {
      const payload = { ...form, port: parseInt(form.port, 10) || 9100 };
      if (editId) await api.put(`/printers/${editId}`, payload);
      else await api.post("/printers", payload);
      toast.success(editId ? "Stampante aggiornata" : "Stampante aggiunta");
      reset(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const edit = (p) => { setEditId(p.id); setForm({ name: p.name, station: p.station, ip_address: p.ip_address || "", port: p.port || 9100, enabled: !!p.enabled }); };
  const del = async (p) => { if (!window.confirm(`Eliminare ${p.name}?`)) return; try { await api.delete(`/printers/${p.id}`); toast.success("Eliminata"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const regenToken = async () => { if (!window.confirm("Rigenerare il token? Dovrai aggiornarlo nel config del Print Agent.")) return; try { const { data } = await api.post("/settings/print-agent-token/regenerate"); setToken(data.token); toast.success("Token rigenerato"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const copy = async (t) => { try { await navigator.clipboard.writeText(t); toast.success("Copiato"); } catch { toast.error("Copia non riuscita"); } };
  const retry = async (j) => { try { await api.post(`/print/jobs/${j.id}/retry`); toast.success("Rimesso in coda"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <Card className="p-6 rounded-xl max-w-2xl mb-6" data-testid="printers-card">
      <div className="flex items-center gap-2 mb-1">
        <Printer className="w-5 h-5 text-primary" />
        <h2 className="font-dash font-semibold text-lg">Stampanti comande (Bar / Cucina)</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Stampanti ESC/POS di rete. La stampa avviene tramite il Print Agent locale, non dal browser. Nessuna stampa fiscale.</p>

      {/* status list */}
      <div className="space-y-2 mb-4">
        {printers.length === 0 && <p className="text-sm text-muted-foreground">Nessuna stampante configurata.</p>}
        {printers.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-2 rounded-lg border border-border p-2.5" data-testid={`printer-row-${p.station}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium">{p.name}</span>
                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{p.station}</span>
                {p.online ? (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-600" data-testid={`printer-online-${p.station}`}><Wifi className="w-3.5 h-3.5" /> ONLINE</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-destructive" data-testid={`printer-offline-${p.station}`}><WifiOff className="w-3.5 h-3.5" /> OFFLINE</span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground truncate">{p.ip_address ? `${p.ip_address}:${p.port}` : "IP non impostato"} · {p.enabled ? "abilitata" : "disabilitata"}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => edit(p)} data-testid={`edit-printer-${p.station}`}><Pencil className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => del(p)} data-testid={`del-printer-${p.station}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      {/* add / edit form */}
      <div className="rounded-xl border border-border p-3 space-y-3 mb-4" data-testid="printer-form">
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Cucina 1" data-testid="printer-name-input" /></div>
          <div><Label className="text-xs">Stazione</Label>
            <Select value={form.station} onValueChange={(v) => setForm({ ...form, station: v })}>
              <SelectTrigger data-testid="printer-station-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="bar">Bar</SelectItem><SelectItem value="cucina">Cucina</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Indirizzo IP (LAN)</Label><Input value={form.ip_address} onChange={(e) => setForm({ ...form, ip_address: e.target.value })} placeholder="es. 192.168.1.50" data-testid="printer-ip-input" /></div>
          <div><Label className="text-xs">Porta</Label><Input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} placeholder="9100" data-testid="printer-port-input" /></div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="printer-enabled-switch" /><Label className="text-sm">Abilitata</Label></div>
          <div className="flex gap-2">
            {editId && <Button variant="ghost" size="sm" onClick={reset}>Annulla</Button>}
            <Button size="sm" className="rounded-full" onClick={save} disabled={busy} data-testid="save-printer-button"><Plus className="w-4 h-4 mr-1" />{editId ? "Salva" : "Aggiungi"}</Button>
          </div>
        </div>
      </div>

      {/* agent token */}
      <div className="rounded-xl border border-border p-3 mb-4">
        <Label className="text-xs text-muted-foreground">Token Print Agent (per il config.yml sul mini-PC)</Label>
        <div className="flex items-center gap-2 mt-1">
          <code className="flex-1 text-xs bg-secondary rounded px-2 py-1.5 truncate" data-testid="print-agent-token">{token || "—"}</code>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => copy(token)} data-testid="copy-print-token"><Copy className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={regenToken} data-testid="regen-print-token"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* failed jobs */}
      {jobs.length > 0 && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-3" data-testid="failed-jobs">
          <p className="text-sm font-medium text-destructive mb-2">Stampe fallite ({jobs.length}) — nessuna comanda è persa</p>
          <div className="space-y-1.5">
            {jobs.map((j) => (
              <div key={j.id} className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate">Tavolo {j.table_number} · {j.kind} · {j.station} {j.course ? `· uscita ${j.course}` : ""}</span>
                <Button size="sm" variant="outline" className="rounded-full h-7" onClick={() => retry(j)} data-testid={`retry-job-${j.id}`}><RotateCcw className="w-3.5 h-3.5 mr-1" /> Riprova stampa</Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
